import { useEffect, useMemo, useState } from 'react';
import {
    jobTypeIpFields,
    speciesIpFields,
    forestInfoIpFields,
    forestInfoSectionDropDownData,
    followUpSectionIpFields,
    followUpSectionDropDownData,
    officeUseSectionIpFields,
} from '../data/EnquiryFormIpFields';
// ✅ REMOVED: transactionTypeOptions, typeOfEnquiryOptions — no longer needed
import useAllocatedToOptions from '../hooks/useAllocatedToOptions';
import useGetUser from 'routes/Auth/useGetUser';
import { toLocalISO } from 'routes/Pages/GenTask/components/SharedFormFields';
import { useToast } from 'context/tostifyContext';
import {
    parseLatLngFromGoogleMapsUrl,
    buildContactPayload,
    buildEnquiryPayload,
    contactToFormPatch,
    enquiryToFormPatch,
} from '../helpers';
import { buildEnquiryEmailHtml, buildEnquiryEmailSubject } from '../enquiryEmailTemplate';

// ✅ UPDATED contact type options
export const CONTACT_TYPE_OPTIONS = ['Private', 'Forester Company', 'Other'];
const NEW_CONTACT_OPTION = { __new: true, crmId: '__new__' };

const DRAFT_KEY = 'timber_enquiry_draft';

const NEW_ENQUIRY_DRAFT_KEY = 'timber_enquiry_draft';
const getDraftKey = crmId => (crmId ? `timber_enquiry_draft_contact_${crmId}` : NEW_ENQUIRY_DRAFT_KEY);

const emptyCheckboxState = fields => Object.fromEntries(fields.map(f => [f.name, false]));

const buildInitialState = initialData => ({
    // ✅ Defaults are now hardcoded per your requirements
    crmTeamId: 'Logwatch',
    crmStageName: 'Enquiries',
    crmStageId: 1,
    crmSalesRep: 'admin@worrellharvesting.ie',

    // contact
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    companyName: '',
    contactType: '',
    contactTypeOther: '', // ✅ NEW: shown when contactType === 'Other'
    homeAddressLine1: '',
    homeAddressLine2: '',
    homeTown: '',
    homeCity: '',
    crmAddressCounty: '',
    crmAddressCountry: '',
    homeCountry: 'Ireland',
    homeZip: '',
    completedBy: initialData.completedBy || '',
    // ✅ REMOVED: crmLinkedInProfile, crmContactWebsite
    crmBackgroundInfo: '',

    // property / forest
    sameAsHome: false,
    siteAddressLine1: '',
    siteAddressLine2: '',
    siteTown: '',
    siteCity: '',
    siteCountry: 'Ireland',
    siteZip: '',
    isForesterManaging: null,
    foresterName: '',
    enquiryOriginatedFrom: '',
    sizeInHectares: '',
    forestDescription: '',
    dropPinUrl: '',
    dropPinLat: null,
    dropPinLng: null,
    hasFellingLicense: null,
    fellingLicenseNumber: '',
    jobType: emptyCheckboxState(jobTypeIpFields),
    species: emptyCheckboxState(speciesIpFields),
    forestryLicenceViewer: '',
    landDirectViewer: '',

    // enquiry details
    typeOfEnquiry: 'Enquiry', // ✅ Now a pill: 'Enquiry' | 'Quick Enquiry'
    enquiryType: '',          // ✅ RENAMED from transactionType
    sendBrochure: false,
    brochureNote: '',
    additionalInfoFromGrower: '',

    // follow up / office use
    followUpDetails: '',
    followUpDate: '',
    // ✅ REMOVED: allocatedTo, pipelineStatus
    enquiryRating: '',
    oldEnquirerTitle: '',
    oldEnquirerFullName: '',

    salesWhNumber: '',
    salesId: null,
    salesCustomerName: '',
    salesForestManager: '',
    salesCustomerEmail: '',
    salesWtgEmail: '',
    salesEmailCopyToCustomer: 'No',
    salesEffectiveFrom: '',
    salesEffectiveTo: '',
    salesTerms: '',
    salesInternalNotes: '',
    salesRows: [],
});

const newSalesRow = () => ({
    rowId: `row_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    id: null, // DB id, filled in when we load from the API
    nameLocation: '',
    productCode: '',
    unitOfMeasure: '',
    eurPerUOM: '',
    quality: '',
    vol: '',
    weeklyVolume: '',
    completed: 'No',
});

const num = value => (value === '' || value == null ? null : Number(value));
const day = value => (value ? String(value).substring(0, 10) : '');

const saleToFormPatch = sale => ({
    salesId: sale.id ?? sale.rows?.[0]?.timberSaleId ?? sale.rows?.[0]?.id ?? null,
    salesRows: (sale.rows ?? []).map(row => ({
        rowId: `srv_${row.timberSaleId ?? row.id}`,
        id: row.timberSaleId ?? row.id,
        nameLocation: row.nameLocation ?? '',
        productCode: row.productCode ?? '',
        unitOfMeasure: row.unitOfMeasure ?? '',
        eurPerUOM: row.eurPerUOM ?? '',
        quality: row.quality ?? '',
        vol: row.vol ?? '',
        weeklyVolume: row.weeklyVolume ?? '',
        completed: row.completed === true || row.completed === 'Yes' ? 'Yes' : 'No',
    })),
});

export default function useEnquiryForm({
    open,
    contacts = [],
    selectedTeam,
    currentStage,
    editingContact = null,
    getEnquiryDetails,
    handleEnquiryformSubmit,
    handleTimberSaleSubmit,
    getTimberSales,
    deleteTimberSaleRow,
    salesReferenceData,
    getContactDocs,
    uploadContactDocs,
    docsState = {},
    teams,
    stages,
    teamMembers
}) {

    const { user } = useGetUser();
    const { showToast } = useToast();
    const { options: allocatedToOptions } = useAllocatedToOptions();

    // ✅ initialData no longer feeds team/stage/rep defaults — those are hardcoded
    const initialData = {
        completedBy: user?.emailAddress || '',
    };

    const [state, setState] = useState(() => buildInitialState(initialData));
    const [activeStep, setActiveStep] = useState(0);
    const [uploadedDocs, setUploadedDocs] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const [pickerValue, setPickerValue] = useState(null);
    const [mode, setMode] = useState(null);
    const [selectedCrmId, setSelectedCrmId] = useState(null);
    const [selectedContactRaw, setSelectedContactRaw] = useState(null);
    const [hasExistingEnquiry, setHasExistingEnquiry] = useState(false);
    const [loadingContactDetails, setLoadingContactDetails] = useState(false);

    const jobTypeKeys = useMemo(() => jobTypeIpFields.map(f => f.name), []);
    const speciesKeys = useMemo(() => speciesIpFields.map(f => f.name), []);

    const draftKey = useMemo(() => getDraftKey(editingContact?.crmId), [editingContact?.crmId]);

    useEffect(() => {
        if ((mode || state.enquiryType === 'Timber Sale') && open) {
            const draft = {
                state, activeStep, mode, pickerValue,
                selectedCrmId, selectedContactRaw, hasExistingEnquiry
            };
            localStorage.setItem(draftKey, JSON.stringify(draft));
            if (draftKey === NEW_ENQUIRY_DRAFT_KEY) {
                window.dispatchEvent(new Event('timberDraftChanged'));
            }
        }
    }, [state, activeStep, mode, pickerValue, selectedCrmId, selectedContactRaw, hasExistingEnquiry, open, draftKey]);

    const reset = () => {
        setState(buildInitialState(initialData));
        setActiveStep(0);
        setUploadedDocs([]);
        setPickerValue(null);
        setMode(null);
        setSelectedCrmId(null);
        setSelectedContactRaw(null);
        setHasExistingEnquiry(false);
        setLoadingContactDetails(false);
    };

    useEffect(() => {
        if (!open) return;

        const contextCrmId = editingContact?.crmId ?? null;
        const key = getDraftKey(contextCrmId);
        const draftStr = localStorage.getItem(key);

        if (editingContact) {
            handleContactPick(editingContact);
        } else if (draftStr) {
            try {
                const draft = JSON.parse(draftStr);
                const draftMatchesContext = contextCrmId ? draft.selectedCrmId === contextCrmId : true;
                if (draftMatchesContext) {
                    setState({ ...buildInitialState(initialData), ...draft.state });
                    setActiveStep(draft.activeStep);
                    setMode(draft.mode);
                    setPickerValue(draft.pickerValue);
                    setSelectedCrmId(draft.selectedCrmId);
                    setSelectedContactRaw(draft.selectedContactRaw);
                    setHasExistingEnquiry(draft.hasExistingEnquiry);
                    setUploadedDocs([]);
                    setLoadingContactDetails(false);
                    if (contextCrmId && getContactDocs) getContactDocs(contextCrmId);
                    if (contextCrmId) showToast('Restored your unsaved changes for this contact', 'success');
                    return;
                }
            } catch (e) {
                console.error('Failed to parse draft', e);
            }
        } else {
            reset();
        }
    }, [open, editingContact?.crmId]);

    // ── Field setters ──────────────────────────────────────────────────────
    const setField = (name, value) => setState(prev => ({ ...prev, [name]: value }));
    const setJobType = (name, checked) => setState(prev => ({ ...prev, jobType: { ...prev.jobType, [name]: checked } }));
    const setSpecies = (name, checked) => setState(prev => ({ ...prev, species: { ...prev.species, [name]: checked } }));

    const setSameAsHome = checked => {
        setState(prev => ({
            ...prev,
            sameAsHome: checked,
            ...(checked
                ? {
                    siteAddressLine1: prev.homeAddressLine1,
                    siteAddressLine2: prev.homeAddressLine2,
                    siteTown: prev.homeTown,
                    siteCity: prev.homeCity,
                    siteCountry: prev.homeCountry,
                    siteZip: prev.homeZip,
                }
                : {}),
        }));
    };

    const HOME_TO_SITE = {
        homeAddressLine1: 'siteAddressLine1',
        homeAddressLine2: 'siteAddressLine2',
        homeTown: 'siteTown',
        homeCity: 'siteCity',
        homeCountry: 'siteCountry',
        homeZip: 'siteZip',
    };
    const setHomeField = (name, value) => {
        setState(prev => {
            const next = { ...prev, [name]: value };
            if (prev.sameAsHome && HOME_TO_SITE[name]) next[HOME_TO_SITE[name]] = value;
            return next;
        });
    };

    const setDropPinUrl = value => {
        const coords = parseLatLngFromGoogleMapsUrl(value);
        setState(prev => ({
            ...prev,
            dropPinUrl: value,
            ...(coords ? { dropPinLat: coords.lat, dropPinLng: coords.lng } : {}),
        }));
    };

    // ── Contact picker ───────────────────────────────────────────────────────
    const handleContactPick = async option => {
        setPickerValue(option);

        if (!option) {
            setMode(null);
            setSelectedCrmId(null);
            setSelectedContactRaw(null);
            setHasExistingEnquiry(false);
            return;
        }

        if (option.__new) {
            setState(prev => ({ ...buildInitialState(initialData), enquiryType: prev.enquiryType }));
            setActiveStep(0);
            setUploadedDocs([]);
            setMode('new');
            setSelectedCrmId(null);
            setSelectedContactRaw(null);
            setHasExistingEnquiry(false);
            return;
        }

        setMode('existing');
        setSelectedCrmId(option.crmId);
        setSelectedContactRaw(option);
        setUploadedDocs([]);
        setActiveStep(0);
        setLoadingContactDetails(true);

        setState(prev => ({
            ...buildInitialState(initialData),
            enquiryType: prev.enquiryType,
            ...contactToFormPatch(option),
        }));

        try {
            const enquiry = getEnquiryDetails ? await getEnquiryDetails(option.crmId) : null;
            if (enquiry) {
                const enquiryPatch = enquiryToFormPatch(enquiry, jobTypeKeys, speciesKeys);

                const safePatch = Object.fromEntries(
                    Object.entries(enquiryPatch).filter(([_, value]) => value !== "" && value !== null)
                );

                setState(prev => ({
                    ...prev,
                    ...safePatch,
                    enquiryType: safePatch.enquiryType || prev.enquiryType || 'Timber Purchase',
                }));
                setHasExistingEnquiry(true);
            } else {
                setHasExistingEnquiry(false);
            }

            if (getTimberSales) {
                const sale = await getTimberSales(option.crmId);
                if (sale) setState(prev => ({ ...prev, ...saleToFormPatch(sale) }));
            }
        } catch (e) {
            showToast('Could not load this contact\u2019s enquiry details', 'error');
            setHasExistingEnquiry(false);
        } finally {
            setLoadingContactDetails(false);
        }

        if (getContactDocs) getContactDocs(option.crmId);
    };

    // ── Steps ────────────────────────────────────────────────────────────────
    const setEnquiryType = value =>
        setState(prev => ({
            ...prev,
            enquiryType: value,
            // don't carry stale brochure data into a General enquiry
            ...(value === 'General' ? { sendBrochure: false, brochureNote: '' } : {}),
        }));

    const isTimberPurchase = state.enquiryType === 'Timber Purchase';
    const isGeneral = state.enquiryType === 'General';
    const isSales = state.enquiryType === 'Timber Sale';

    const showPropertyStep = isTimberPurchase && (state.typeOfEnquiry === 'Enquiry' || state.typeOfEnquiry === 'All');
    const showBrochure = !isGeneral;
    const showFollowUpStep = state.typeOfEnquiry !== 'LandRegistryViewer';
    const showOfficeUse = state.typeOfEnquiry === 'All';

    const steps = useMemo(() => {
        const s = [{ key: 'contact', label: 'Contact' }];
        if (showPropertyStep) s.push({ key: 'property', label: 'Property & Forest' });
        s.push({ key: 'enquiry', label: 'Enquiry Details' });
        // ✅ REMOVED: notes step
        return s;
    }, [showPropertyStep, showFollowUpStep]);

    useEffect(() => {
        if (activeStep > steps.length - 1) setActiveStep(steps.length - 1);
    }, [steps, activeStep]);

    const goNext = () => setActiveStep(s => Math.min(s + 1, steps.length - 1));
    const goBack = () => setActiveStep(s => Math.max(s - 1, 0));

    // ── Submit ─────────────────────────────────────────────────────────────
    const submit = async () => {
        console.log("state ===", state.enquiryType === 'Timber Sale', state.enquiryType);
        if (state.enquiryType === 'Timber Sale') return submitSales();
        if (state.enquiryType !== 'Timber Sale' && !mode) {
            showToast('Select an existing contact or create a new one first', 'error');
            return;
        }
        if (!state.firstName?.trim()) {
            showToast('First Name is required', 'error');
            setActiveStep(0);
            return;
        }

        const todaysDate = toLocalISO();

        const contactPayload = buildContactPayload({
            state,
            isNew: mode === 'new',
            baseContact: selectedContactRaw,
            teamId: state.crmTeamId,   // ✅ use hardcoded team from state
            stage: { label: state.crmStageName, StageId: state.crmStageId },
        });

        const enquiryPayload = buildEnquiryPayload({
            state,
            crmId: mode === 'existing' ? selectedCrmId : 0,
            todaysDate,
            jobTypeKeys,
            speciesKeys,
        });

        const endpoint = hasExistingEnquiry ? '/Enquiry/EditEnquiryDetailsNew' : '/Enquiry/AddEnquiryDetails';

        setSubmitting(true);
        try {
            const notifyEmail = !hasExistingEnquiry
                ? {
                    subject: buildEnquiryEmailSubject({ state, hasExistingEnquiry }),
                    html: buildEnquiryEmailHtml({
                        state,
                        isNewContact: mode === 'new',
                        hasExistingEnquiry,
                        jobTypeIpFields,
                        speciesIpFields,
                        forestInfoIpFields,
                        followUpSectionIpFields,
                        officeUseSectionIpFields,
                    }),
                    recipients: ['ashish.garg@dscission.com'].filter(Boolean),
                }
                : null;

            const finalCrmId = await handleEnquiryformSubmit(enquiryPayload, endpoint, {
                crmId: mode === 'existing' ? selectedCrmId : null,
                contactPayload,
                notifyEmail,
            });

            if (finalCrmId && uploadedDocs.length > 0 && uploadContactDocs) {
                await uploadContactDocs(finalCrmId, uploadedDocs, 'file');
            }

            localStorage.removeItem(draftKey);
            if (draftKey === NEW_ENQUIRY_DRAFT_KEY) {
                window.dispatchEvent(new Event('timberDraftChanged'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Timber submit
    // ── Timber Sales rows ───────────────────────────────────────────────────
    const addSalesRow = () =>
        setState(prev => ({ ...prev, salesRows: [...(prev.salesRows ?? []), newSalesRow()] }));

    const updateSalesRow = (rowId, name, value) =>
        setState(prev => ({
            ...prev,
            salesRows: (prev.salesRows ?? []).map(r => (r.rowId === rowId ? { ...r, [name]: value } : r)),
        }));

    const removeSalesRow = async row => {
        if (row.id) {
            const ok = await deleteTimberSaleRow?.(row.id);
            if (!ok) return false;
        }
        setState(prev => ({ ...prev, salesRows: (prev.salesRows ?? []).filter(r => r.rowId !== row.rowId) }));
        return true;
    };

    const submitSales = async () => {
        if (!state.salesCustomerName?.trim()) return showToast('Customer is required', 'error');
        if (!state.salesForestManager?.trim()) return showToast('Forest Manager is required', 'error');
        if (!state.salesRows?.length) return showToast('Add at least one price & volume row', 'error');
        if (selectedTeam?.value == null || selectedTeam.value === '') return showToast('CRM team is required', 'error');

        const isExistingSale = Boolean(state.salesId && selectedCrmId);
        const contactState = {
            ...state,
            firstName: state.salesForestManager,
            lastName: '',
            crmSalesRep: 1,
            contactType: 'Other',
            contactTypeOther: 'Other',
        };

        const contactPayload = buildContactPayload({
            state: contactState,
            isNew: !isExistingSale,
            baseContact: isExistingSale ? selectedContactRaw : null,
            teamId: selectedTeam.value,
            stage: { label: state.crmStageName, StageId: state.crmStageId },
        });

        const enquiryData = buildEnquiryPayload({
            state: contactState,
            crmId: isExistingSale ? selectedCrmId : 0,
            todaysDate: toLocalISO(),
            jobTypeKeys,
            speciesKeys,
        });

        const sales = {
            id: state.salesId,
            rows: state.salesRows.map(({ rowId, ...r }) => ({
                ...r,
                eurPerUOM: num(r.eurPerUOM),
                vol: num(r.vol),
                weeklyVolume: num(r.weeklyVolume),
                completed: r.completed === 'Yes',
            })),
        };

        setSubmitting(true);
        try {
            const result = await handleTimberSaleSubmit(
                {
                    crmId: isExistingSale ? selectedCrmId : null,
                    contactPayload,
                    enquiryData,
                    enquiryEndpoint: isExistingSale && hasExistingEnquiry ? '/Enquiry/EditEnquiryDetailsNew' : '/Enquiry/AddEnquiryDetails',
                    sales,
                },
                uploadedDocs,
            );
            if (!result?.crmId) return;

            setMode('existing');
            setSelectedCrmId(result.crmId);
            setHasExistingEnquiry(true);
            setUploadedDocs([]);

            const fresh = await getTimberSales?.(result.crmId);
            if (fresh) setState(prev => ({ ...prev, ...saleToFormPatch(fresh) }));

            localStorage.removeItem(draftKey);
            if (draftKey === NEW_ENQUIRY_DRAFT_KEY) window.dispatchEvent(new Event('timberDraftChanged'));
        } finally {
            setSubmitting(false);
        }
    };

    const contactOptions = useMemo(() => [NEW_CONTACT_OPTION, ...contacts], [contacts]);
    const existingDocs = selectedCrmId ? docsState?.[selectedCrmId]?.file || [] : [];

    return {
        state,
        setField,
        setJobType,
        setSpecies,
        setSameAsHome,
        setHomeField,
        setDropPinUrl,

        steps,
        activeStep,
        setActiveStep,
        goNext,
        goBack,
        showPropertyStep,
        showFollowUpStep,
        showOfficeUse,

        mode,
        pickerValue,
        handleContactPick,
        contactOptions,
        hasExistingEnquiry,
        loadingContactDetails,
        selectedCrmId,

        uploadedDocs,
        setUploadedDocs,
        existingDocs,
        submitting,
        submit,

        // reference data
        allocatedToOptions,
        // ✅ REMOVED: typeOfEnquiryOptions, transactionTypeOptions
        forestInfoIpFields,
        forestInfoSectionDropDownData,
        followUpSectionIpFields,
        followUpSectionDropDownData,
        officeUseSectionIpFields,
        jobTypeIpFields,
        speciesIpFields,

        teams,
        stages,
        teamMembers,

        isEditMode: Boolean(editingContact),

        enquiryType: state.enquiryType,
        setEnquiryType,
        isTimberPurchase,
        isGeneral,
        isSales,
        showBrochure,

        addSalesRow,
        updateSalesRow,
        removeSalesRow,
        salesOptions: {
            products: salesReferenceData?.products ?? [],
        },
    };
}